<template>

  <div
    :class="{ 'creditcard--flipped': (isFlipped && enablesBackface) }"
    class="creditcard"
  >

    <div class="creditcard__inner">

      <div class="creditcard__frontface">
        <div class="creditcard__frontface__content">
          <span class="creditcard__logo">

            <slot name="brandIcon">

              <template v-if="cardBrand.length">
                <component v-bind:is="iconSvgComponentName"/>
              </template>
            </slot>
          </span>

          <div class="creditcard__number">

            <span
              v-for="(item, index) in getCardNumberArray"
              :key="index"
            >
              {{ item }}
            </span>
          </div>

          <div class="creditcard__footer">

            <span class="creditcard__name">
              {{ cardHolder  && cardHolder.length ? cardHolder : 'Nome do titular' }}
            </span>

            <span class="creditcard__date">
              {{ cardExpirationDate ? cardExpirationDate : 'MM/AA' }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="enablesBackface"
        class="creditcard__backface"
      >

        <div class="creditcard__backface__content">
          <span class="creditcard__backface__blackstripe"/>

          <div class="creditcard__code">
            <span>{{ cardSecurityCode }}</span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'CreditCard',
  props: {
    card: {
      cardNumber: String,
      expirationDate: String,
      holder: String,
      securityCode: String,
      brand: String,
    },

    isFlipped: {
      type: Boolean,
      default: false,
    },

    enablesBackface: {
      type: Boolean,
      default: true,
    }
  },
  computed: {
    cardNumber() {
      return this.hasPropertyWithString('cardNumber');
    },
    cardExpirationDate() {
      return this.hasPropertyWithString('expirationDate');
    },
    cardHolder() {
      return this.hasPropertyWithString('holder');
    },
    cardSecurityCode() {
      return this.card && this.card.securityCode && this.card.securityCode.length >= 3
        ? `**${this.card.securityCode.substring(this.card.securityCode.length - 1)}`
        : '***';
    },
    cardBrand() {
      return this.hasPropertyWithString('brand');
    },
    getCardNumberArray() {
      const whiteSpacedString = (this.cardNumber.length < 13)
        ? '**** **** **** ****'
        : `**** **** **** ${this.cardNumber.toString().substring(this.cardNumber.length - 4)}`;

      return whiteSpacedString.split(' ');
    },
    iconSvgComponentName() {
      return `CardLogo${this.normalizedBrandName(this.cardBrand)}Svg`;
    },
  },
  methods: {
    hasPropertyWithString(prop, defaultReturn = '') {
      return (this.card && typeof this.card[prop] === 'string') ? this.card[prop] : defaultReturn;
    },
    maskedCardNumber(cardNumber) {
      if (cardNumber.length === 15) {
        return cardNumber.replace(/^(\d{1,4})(\d{1,6})(\d{1,5}).*/g, '$1 $2 $3');
      }

      return cardNumber.replace(/^(\d{4})(\d{4})(\d{4})(\d{4}).*/g, '$1 $2 $3 $4');
    },

    normalizedBrandName(string) {
      const splittedString = string.split('-');

      const normalizedBrandName = splittedString.length > 1
        ? (this.capitalizedString(splittedString[0]) + this.capitalizedString(splittedString[1]))
        : this.capitalizedString(splittedString[0]);

      return normalizedBrandName;
    },

    capitalizedString(string) {
      const capitalized = string
        ? string[0].toUpperCase() + string.slice(1).toLowerCase()
        : string;

      return capitalized;
    }
  },
};
</script>

<style lang="scss" scoped>
.creditcard {
  display: flex;
  height: 170px;
  margin: 20px auto 0;
  max-width: 312px;
  perspective: 1000px;
  width: 100%;

  &__inner,
  &__frontface,
  &__backface {
    display: flex;
    height: 100%;
    width: 100%;
  }

  &__inner {
    position: relative;
    transform-style: preserve-3d;
    -moz-transform-style: preserve-3d;
    transition: transform 0.6s;
  }

  @supports (-moz-appearance:none) {
   &__inner {
     position: relative;
    transform-style: preserve-3d;
    -moz-transform-style: preserve-3d;
    transition: transform 0.6s;
    backface-visibility: hidden;
   }
  }

  &__frontface,
  &__backface {
    backface-visibility: hidden;
    background-color: var(--color-grey-10);
    border-radius: 0.75rem;
    flex-direction: column;
    overflow: hidden;
    padding: 24px;
    position: absolute;

    &:before {
      background-color: rgba(196, 196, 196, 0.05);
      border-radius: 50%;
      content: "";
      height: 240px;
      left: -20%;
      position: absolute;
      top: -18%;
      width: 240px;
      z-index: 0;
    }

    &__content {
      display: flex;
      flex-direction: column;
      position: relative;
      width: 100%;
      z-index: 1;
    }
  }

  &__backface {
    padding: 20px 0;
    transform: rotateY(180deg);

    &__blackstripe {
      background: black;
      height: 38px;
      width: 100%;
    }
  }

  &--flipped &__inner {
    transform: rotateY(180deg);
  }

  &__number,
  &__name,
  &__date {
    display: flex;
    height: 24px;
  }

  &__logo {
    height: 35px;
    margin-bottom: 20px;
    max-width: 50px;
  }

  &__number,
  &__footer {
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  &__number {
    font-size: var(--font-size-5);
    margin-bottom: 25px;
    text-align: right;

    & span {
      color: white;
    }
  }

  &__footer {
    color: white;
    display: flex;
    font-size: var(--font-size-2);
  }

  &__name {
    width: 70%;
  }

  &__date {
    width: 30%;
  }

  &__code {
    align-items: center;
    align-self: center;
    background-color: white;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    font-size: var(--font-size-4);
    height: 38px;
    justify-content: flex-end;
    margin-top: 20px;
    padding-right: 10px;
    width: 80%;
  }
}
</style>
